import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { File as IconoArchivo } from 'lucide-react';
import { formatoFecha, tamanoLegible } from '../../../lib/formularios';
import { CORREO_URL } from '../../../lib/dominios';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { estiloTooltip, paletaDe, porcentaje, useColoresGrafica } from './colores';

/*
 * La gráfica (o la lista) del resumen de UNA pregunta, según su tipo:
 *   opción múltiple / desplegable → pastel · casillas → barras horizontales ·
 *   escala / calificación → barras verticales · cuadrículas → barras agrupadas ·
 *   texto, fecha, hora → lista de respuestas con cuántas veces se repiten · archivos → lista.
 * `datos` es la entrada de `resumen.preguntas[item.id]` (contrato en claude/formularios.md).
 */

const recortar = (texto, n = 22) => {
  const s = String(texto ?? '');
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

function Otros({ otros }) {
  if (!otros?.length) return null;
  return (
    <div className="rs-otros">
      <div className="rs-subtitulo">Otras respuestas</div>
      <ul className="rs-lista fm-scroll">
        {otros.map((o, i) => <li key={`${o}-${i}`} className="rs-lista-fila">{o}</li>)}
      </ul>
    </div>
  );
}

function Pastel({ datos, paleta, colores }) {
  const conteos = datos.conteos || [];
  const total = conteos.reduce((s, c) => s + (c.n || 0), 0);
  const conDatos = conteos.map((c, i) => ({ ...c, color: paleta[i % paleta.length] })).filter((c) => c.n > 0);
  return (
    <div className="rs-pastel">
      <div className="rs-pastel-grafica">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={conDatos}
              dataKey="n"
              nameKey="texto"
              outerRadius="92%"
              stroke={colores.panel}
              strokeWidth={2}
              isAnimationActive={false}
              labelLine={false}
              label={({ percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
                if (percent < 0.06) return null;
                const r = innerRadius + (outerRadius - innerRadius) * 0.62;
                const x = cx + r * Math.cos((-midAngle * Math.PI) / 180);
                const y = cy + r * Math.sin((-midAngle * Math.PI) / 180);
                return (
                  <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
                    {`${Math.round(percent * 1000) / 10}%`}
                  </text>
                );
              }}
            >
              {conDatos.map((c) => <Cell key={c.id} fill={c.color} />)}
            </Pie>
            <Tooltip {...estiloTooltip(colores)} formatter={(n) => [`${n} (${porcentaje(n, total)})`, 'Respuestas']} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="rs-leyenda">
        {conteos.map((c, i) => (
          <li key={c.id} className="rs-leyenda-fila">
            <span className="rs-leyenda-color" style={{ background: paleta[i % paleta.length] }} aria-hidden="true" />
            <span className="rs-leyenda-texto">{c.texto}</span>
            <span className="rs-leyenda-n">{c.n} · {porcentaje(c.n, total)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BarrasHorizontales({ datos, respondidas, color, colores }) {
  const conteos = datos.conteos || [];
  const alto = Math.max(110, conteos.length * 38 + 36);
  // En el teléfono la etiqueta de cada opción y la cifra se comen la barra: se estrechan
  const movil = useIsMobile(640);
  return (
    <ResponsiveContainer width="100%" height={alto}>
      <BarChart data={conteos} layout="vertical" margin={{ top: 4, right: movil ? 58 : 72, bottom: 4, left: 4 }}>
        <CartesianGrid horizontal={false} stroke={colores.linea} />
        <XAxis type="number" allowDecimals={false} tick={{ fill: colores.texto2, fontSize: 12 }} stroke={colores.linea2} />
        <YAxis
          type="category"
          dataKey="texto"
          width={movil ? 84 : 128}
          tick={{ fill: colores.texto2, fontSize: 12 }}
          tickFormatter={(t) => recortar(t, movil ? 11 : 18)}
          stroke={colores.linea2}
        />
        <Tooltip {...estiloTooltip(colores)} formatter={(n) => [`${n} (${porcentaje(n, respondidas)})`, 'Respuestas']} />
        <Bar dataKey="n" fill={color} radius={[0, 4, 4, 0]} isAnimationActive={false} maxBarSize={26}>
          <LabelList
            dataKey="n"
            position="right"
            fill={colores.texto2}
            fontSize={12}
            formatter={(n) => `${n} (${porcentaje(n, respondidas)})`}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function BarrasVerticales({ item, datos, respondidas, color, colores }) {
  const filas = useMemo(() => {
    const porValor = Object.fromEntries((datos.conteos || []).map((c) => [String(c.id), c.n]));
    const min = item.tipo === 'escala' ? (item.escala?.min ?? 1) : 1;
    const max = item.tipo === 'escala' ? (item.escala?.max ?? 5) : (item.calificacion?.max ?? 5);
    const out = [];
    for (let v = min; v <= max; v += 1) out.push({ valor: String(v), n: porValor[String(v)] || 0 });
    return out;
  }, [item, datos]);
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={filas} margin={{ top: 22, right: 8, bottom: 4, left: -18 }}>
        <CartesianGrid vertical={false} stroke={colores.linea} />
        <XAxis dataKey="valor" tick={{ fill: colores.texto2, fontSize: 12 }} stroke={colores.linea2} />
        <YAxis allowDecimals={false} tick={{ fill: colores.texto2, fontSize: 12 }} stroke={colores.linea2} />
        <Tooltip {...estiloTooltip(colores)} formatter={(n) => [`${n} (${porcentaje(n, respondidas)})`, 'Respuestas']} />
        <Bar dataKey="n" fill={color} radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={56}>
          <LabelList
            dataKey="n"
            position="top"
            fill={colores.texto2}
            fontSize={11}
            formatter={(n) => (n ? `${n} (${porcentaje(n, respondidas)})` : '')}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function BarrasAgrupadas({ item, datos, paleta, colores }) {
  const columnas = item.columnas || [];
  const filas = (datos.filas || []).map((f) => {
    const fila = { texto: f.texto };
    (f.conteos || []).forEach((c) => { fila[c.id] = c.n; });
    columnas.forEach((col) => { if (fila[col.id] === undefined) fila[col.id] = 0; });
    return fila;
  });
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={filas} margin={{ top: 8, right: 8, bottom: 4, left: -18 }}>
        <CartesianGrid vertical={false} stroke={colores.linea} />
        <XAxis dataKey="texto" tick={{ fill: colores.texto2, fontSize: 12 }} tickFormatter={(t) => recortar(t, 14)} stroke={colores.linea2} />
        <YAxis allowDecimals={false} tick={{ fill: colores.texto2, fontSize: 12 }} stroke={colores.linea2} />
        <Tooltip {...estiloTooltip(colores)} />
        <Legend wrapperStyle={{ fontSize: 12, color: colores.texto2 }} />
        {columnas.map((col, i) => (
          <Bar key={col.id} dataKey={col.id} name={col.texto} fill={paleta[i % paleta.length]} radius={[3, 3, 0, 0]} isAnimationActive={false} maxBarSize={28} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function ListaValores({ item, datos }) {
  const valores = datos.valores || [];
  if (!valores.length) return <p className="rs-vacio">Aún no hay respuestas a esta pregunta.</p>;
  return (
    <ul className="rs-lista fm-scroll">
      {valores.map((v, i) => (
        <li key={`${v.valor}-${i}`} className="rs-lista-fila">
          <span className="rs-lista-valor">{item.tipo === 'fecha' ? formatoFecha(v.valor) : String(v.valor)}</span>
          {v.n > 1 && <span className="rs-lista-n" aria-label={`${v.n} veces`}>×{v.n}</span>}
        </li>
      ))}
    </ul>
  );
}

function ListaArchivos({ datos }) {
  const archivos = datos.archivos || [];
  if (!archivos.length) return <p className="rs-vacio">Aún no se subieron archivos.</p>;
  return (
    <ul className="rs-archivos">
      {archivos.map((a) => (
        <li key={a.id}>
          <a className="rs-archivo" href={`${CORREO_URL}/file/${a.id}`} target="_blank" rel="noopener noreferrer">
            <IconoArchivo className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="rs-archivo-nombre">{a.nombre}</span>
            <span className="rs-archivo-tamano">{tamanoLegible(a.tamano)}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function GraficaPregunta({ item, datos, colorTema, publico = false }) {
  const colores = useColoresGrafica();
  const paleta = useMemo(() => paletaDe(colorTema), [colorTema]);
  if (!datos || !datos.respondidas) return <p className="rs-vacio">Aún no hay respuestas a esta pregunta.</p>;
  const respondidas = datos.respondidas;

  switch (item.tipo) {
    case 'opcion_multiple':
    case 'desplegable':
      return (
        <>
          <Pastel datos={datos} paleta={paleta} colores={colores} />
          <Otros otros={datos.otros} />
        </>
      );
    case 'casillas':
      return (
        <>
          <BarrasHorizontales datos={datos} respondidas={respondidas} color={paleta[0]} colores={colores} />
          <Otros otros={datos.otros} />
        </>
      );
    case 'escala':
    case 'calificacion':
      return <BarrasVerticales item={item} datos={datos} respondidas={respondidas} color={paleta[0]} colores={colores} />;
    case 'cuadricula_opcion':
    case 'cuadricula_casillas':
      return <BarrasAgrupadas item={item} datos={datos} paleta={paleta} colores={colores} />;
    case 'archivo':
      return publico ? <p className="rs-vacio">Los archivos subidos solo los ve quien creó el formulario.</p> : <ListaArchivos datos={datos} />;
    default:
      return <ListaValores item={item} datos={datos} />;
  }
}
